import React from 'react';
import DiceItem from '@/common-pages/dice/component/dice-item';
import Ball from '@/common-pages/dice/component/ball';
import {useTranslation} from 'react-i18next';
import Won from '../won/won';

interface ListItem {
  amount: number;
  awardAmount: number;
  number: string;
  playType: string;
  type: string;
}

export interface DiceResultProps {
  isOpen?: boolean;
  result: string;
  bigOrSmall: number;
  oddOrEven: number;
  totalCount: number;
  typeList: ListItem[];
}

const DiceResult = ({
  result = '',
  totalCount = 1,
  bigOrSmall,
  isOpen = false,
  oddOrEven,
  typeList = [],
}: DiceResultProps) => {
  const {t} = useTranslation();

  const winList = React.useMemo(() => {
    return typeList.filter(item => item.awardAmount > 0);
  }, [typeList]);

  const sum_type = React.useMemo(() => {
    return typeList.filter(
      item => item.playType === 'Sum' && isNaN(Number(item.number)),
    );
  }, [typeList]);

  const sum_point = React.useMemo(() => {
    return typeList.filter(
      item => item.playType === 'Sum' && !isNaN(Number(item.number)),
    );
  }, [typeList]);

  const single = React.useMemo(() => {
    return typeList.filter(item => item.playType === 'Single');
  }, [typeList]);

  const double = React.useMemo(() => {
    return typeList.filter(item => item.playType === 'Pair');
  }, [typeList]);
  const triple = React.useMemo(() => {
    return typeList.filter(item => item.playType === 'Triple');
  }, [typeList]);

  const renderItem = (info: ListItem, key?: number) => {
    if (info.playType === 'Sum' && isNaN(Number(info.number))) {
      return (
        <Ball
          isNum={false}
          type={info.number.toLowerCase()}
          ballSize={24}
          key={key}
        />
      );
    }
    if (info.playType === 'Sum' && !isNaN(Number(info.number))) {
      return <Ball num={+info.number} ballSize={24} fontSize={14} key={key} />;
    }
    if (info.playType === 'Single') {
      return <DiceItem diceSize={24} diceNum={info.number} key={key} />;
    }
    if (info.playType === 'Pair') {
      return (
        <div className="flex" key={key}>
          {info.number.split('').map((pie, _ii) => (
            <DiceItem diceSize={24} diceNum={pie} key={_ii} />
          ))}
        </div>
      );
    }
    if (info.playType === 'Triple') {
      return (
        <div className="flex" key={key}>
          {info.number.split('').map((pie, _ii) => (
            <DiceItem diceSize={24} diceNum={pie} key={_ii} />
          ))}
        </div>
      );
    }
    return null;
  };

  const renderGroup = (label: string, list: ListItem[]) => {
    if (!list.length) {
      return null;
    }
    return (
      <div className="flex w-fit items-center gap-2 rounded-[1.25rem] bg-[#F6F7FB] px-3 py-1">
        <span className="text-sm font-bold">{label}：</span>
        <div className="flex gap-2">
          {list.map((item, _i) => renderItem(item, _i))}
        </div>
      </div>
    );
  };

  return (
    <div className="mt-2 flex flex-col gap-2">
      {isOpen && (
        <div className="flex items-center justify-between bg-[#F4F4F4] px-3 py-2">
          <div className="flex-1">
            <span className="text-sm font-bold">
              {t('bets-detail.label.drawRes')}：
            </span>
          </div>
          <div className="flex items-center gap-1">
            {result.split(',').map((item, _i) => (
              <DiceItem diceSize={32} diceNum={item} key={_i} />
            ))}
            <div className="h-8 w-px bg-[#D8E0EA]" />
            <Ball fontSize={16} ballSize={32} isNum num={totalCount} />
            <div className="h-8 w-px bg-[#D8E0EA]" />
            {bigOrSmall && bigOrSmall > 0 ? (
              <Ball
                isNum={false}
                type={bigOrSmall === 1 ? 'big' : 'small'}
                fontSize={16}
                ballSize={32}
              />
            ) : null}
            {oddOrEven && oddOrEven > 0 ? (
              <Ball
                isNum={false}
                type={oddOrEven === 2 ? 'even' : 'odd'}
                fontSize={16}
                ballSize={32}
              />
            ) : null}
          </div>
        </div>
      )}
      {winList.length > 0 &&
        winList.map((item, _i) => (
          <Won
            key={_i}
            awardAmount={item.awardAmount}
            bet={item.amount}
            result={renderItem(item)}
          />
        ))}
      {renderGroup(t('game-page.label.sumType'), sum_type)}
      {renderGroup(t('game-page.label.sumPoints'), sum_point)}
      {renderGroup(t('game-page.label.single'), single)}
      {renderGroup(t('game-page.label.double'), double)}
      {renderGroup(t('game-page.label.triple'), triple)}
    </div>
  );
};

export default DiceResult;
